import { useEffect, useState } from 'react';

/**
 * Tiny dependency-free router with two modes:
 *
 * - Path mode (http/https): real URLs like /wiki/foundry/ via the history API.
 *   These are what search engines and AI crawlers index; the build prerenders a
 *   static HTML file for every route so deep links work on any static host with
 *   no rewrite rules.
 * - Hash mode (file:// and other non-http protocols): #/wiki style, for the
 *   Tauri shell and zip distributions where there is no server at all.
 *
 * Old #/wiki links are redirected to /wiki/ on load in path mode, so nothing
 * shared before the switch breaks.
 */
const HASH_MODE = typeof window !== 'undefined' && !/^https?:$/.test(window.location.protocol);

function normalize(path: string): string {
  let p = path.split('?')[0]!.split('#')[0]!;
  if (!p.startsWith('/')) p = '/' + p;
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p;
}

/** The href to put in an <a> for an internal route (crawlable in path mode). */
export function hrefFor(path: string): string {
  const p = normalize(path);
  if (HASH_MODE) return `#${p}`;
  return p === '/' ? '/' : `${p}/`;
}

function current(): string {
  if (HASH_MODE) {
    const h = window.location.hash.replace(/^#/, '');
    return normalize(h || '/');
  }
  return normalize(window.location.pathname);
}

// Legacy hash links (#/wiki) arriving in path mode get a clean replaceState.
if (typeof window !== 'undefined' && !HASH_MODE && window.location.hash.startsWith('#/')) {
  window.history.replaceState(null, '', hrefFor(window.location.hash.slice(1)));
}

const listeners = new Set<() => void>();
function emit(): void {
  for (const l of listeners) l();
}

/** Programmatic navigation. Works in both modes; scrolls to top like a page load. */
export function go(path: string): void {
  const p = normalize(path);
  if (p === current()) return;
  if (HASH_MODE) {
    window.location.hash = p;
  } else {
    window.history.pushState(null, '', hrefFor(p));
    emit();
  }
  window.scrollTo(0, 0);
}

export function useRoute(): string {
  const [route, setRoute] = useState(current);
  useEffect(() => {
    const on = () => setRoute(current());
    listeners.add(on);
    window.addEventListener('popstate', on);
    window.addEventListener('hashchange', on);
    return () => {
      listeners.delete(on);
      window.removeEventListener('popstate', on);
      window.removeEventListener('hashchange', on);
    };
  }, []);
  return route;
}

/**
 * Internal link. Renders a real <a href> (so crawlers and middle-click work)
 * and routes in-app on plain left clicks.
 */
export function A({
  to,
  children,
  onClick,
  ...rest
}: { to: string } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  return (
    <a
      href={hrefFor(to)}
      onClick={(e) => {
        onClick?.(e);
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        go(to);
      }}
      {...rest}
    >
      {children}
    </a>
  );
}
